/**
 * Chords that are inactive because they lost to ANOTHER command's saved
 * override (Slice 3 review, N1 generalized by O1). When a row that still used
 * its defaults is saved — the user's own edit, or a durable loss — these
 * chords are left out: the row keeps the outcome that was already in effect
 * instead of promoting them over the override that made them inactive.
 * Reserved chords and same-row duplicates are not such losses and are kept.
 */
import type { KeybindingCommandId, KeybindingNotice } from './keybindingTypes';

/** Stored overrides by command id, as the store and `validateKeymap` hold them. */
export type StoredOverrides = { readonly [commandId: string]: readonly string[] | null | undefined };

const LOST_TO_ANOTHER_COMMAND: ReadonlySet<KeybindingNotice['kind']> = new Set<KeybindingNotice['kind']>([
  'default-shadowed', 'layout-collision', 'collision',
]);

/**
 * A non-durable notice saying its chord lost to another command's saved
 * override: a default that override uses (`default-shadowed`), a
 * layout-dependent overlap, or a conflict that exists only on this platform.
 */
export function losesToSavedOverride(notice: KeybindingNotice, overrides: StoredOverrides): boolean {
  const winner = notice.relatedCommandId;
  return LOST_TO_ANOTHER_COMMAND.has(notice.kind) && !notice.persisted &&
    winner !== undefined && winner !== notice.commandId && Array.isArray(overrides[winner]);
}

/** The chords of `commandId` that lost to another command's saved override. */
export function chordsLostToSavedOverrides(
  notices: readonly KeybindingNotice[],
  commandId: KeybindingCommandId,
  overrides: StoredOverrides,
): ReadonlySet<string> {
  return new Set(notices
    .filter((notice) => notice.commandId === commandId && losesToSavedOverride(notice, overrides))
    .map(({ chord }) => chord));
}
